import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { MaskLines, TechLabel, Reveal } from "@/components/Reveal";
import { CtaPrimary, CtaGhost } from "@/components/CTA";
import { CTASection } from "@/components/Sections";
import { IMG, BRANDS } from "@/lib/site";

const TRUST = [
  { label: "Trusted Global Suppliers", sub: "Europe · Japan · Korea · Mexico · Turkey" },
  { label: "Reliable Supply", sub: "Consistent lead times" },
  { label: "Competitive Pricing", sub: "Original & aftermarket parts" },
  { label: "Mining Spare & Wear Parts", sub: "Across the flowsheet" },
];

const WHAT_WE_DO = [
  {
    tag: "Rock Drilling Tools",
    title: "Supplying top-quality rock drilling tools",
    copy: "Drill bits, DTH hammers and bits, extension and tunneling rods, shank adapters and other drilling tool accessories.",
    img: "https://static.prod-images.emergentagent.com/jobs/6d522951-99d7-40e7-a813-14665806a45d/images/e114549df8348816e8738782ff7beaf753acbf464399a81cbeacacfe30a204b7.jpeg",
  },
  {
    tag: "Spare Parts",
    title: "Spare Parts",
    copy: "Supplying quality OEM, aftermarket spare parts for drilling m/c, wear and spare parts for crushing line equipment, conveyor components, shaft hoisting...",
    img: "https://customer-assets-agu9un31.emergentagent.net/job_6d522951-99d7-40e7-a813-14665806a45d/artifacts/l2ouxs9w_WhatsApp%20Image%202026-08-25%20at%2014.09.05.jpeg",
  },
  {
    tag: "Mineral Processing",
    title: "Centrifugal Pumps",
    copy: "Complete range of pumps for medium to heavy duty slurry operations. Mill discharge, flotation feed, thickener feed and tailing pumps completely customized for your application.",
    img: "https://static.prod-images.emergentagent.com/jobs/6d522951-99d7-40e7-a813-14665806a45d/images/120ac8bbac009219554f72a1a82b48b0a34e2d8162559339fcfcce77c4d563b3.jpeg",
  },
  {
    tag: "Construction",
    title: "Construction",
    copy: "Supporting construction projects with reliable spare parts and equipment solutions across wastewater infrastructure, tunnelling, roads, aggregates and quarry operations.",
    img: "https://images.unsplash.com/photo-1674578745937-c73f81bdda07?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHw0fHx3YXN0ZXdhdGVyJTIwdHJlYXRtZW50JTIwcGxhbnQlMjBhZXJpYWx8ZW58MHx8fHwxNzg4OTY0NTY3fDA&ixlib=rb-4.1.0&q=85",
  },
];

const PROCESS = ["Failure", "Response", "Part", "Install", "Uptime"];

function Hero() {
  return (
    <section className="relative pt-32 pb-16 lg:pt-40 lg:pb-24 overflow-hidden" data-testid="hero">
      <div className="absolute inset-0 grid-bg opacity-40" />
      <div className="relative max-w-[1600px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-7">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }}>
            <TechLabel>Mining Equipment Spare &amp; Wear Parts</TechLabel>
          </motion.div>

          <h1 className="font-display font-black uppercase tracking-tight text-offwhite text-[clamp(2.2rem,7.5vw,3rem)] sm:text-6xl lg:text-[5.2rem] leading-[0.95] lg:leading-[0.9] mt-6 break-words">
            <MaskLines lines={["The Right Parts.", "Reliable Supply.", "Better Uptime."]} delay={0.25} />
          </h1>

          <Reveal delay={0.75} className="max-w-2xl">
            <p className="text-mutedgray text-lg md:text-xl mt-8 leading-relaxed">
              Premium spare and wear parts for mining equipment, sourced from trusted global manufacturers and backed by reliable supply.
            </p>
          </Reveal>
        </div>

        <div className="lg:col-span-5">
          <Reveal delay={0.3}>
            <div className="relative overflow-hidden border border-offwhite/10">
              {/* Same looping mine footage on all devices */}
              <motion.video
                className="w-full aspect-[4/5] object-cover"
                src="/hero-mine.mp4"
                poster="/hero-poster.jpg"
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                data-testid="hero-video"
                initial={{ scale: 1.05 }}
                animate={{ scale: [1.05, 1.12, 1.05] }}
                transition={{ duration: 20, ease: "easeInOut", repeat: Infinity }}
              />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  useSEO({
    title: "BlackSilex | Mining Equipment Spare Parts & Wear Parts",
    description: "BlackSilex supplies spare and wear parts for mining equipment, including original and aftermarket components, with reliable sourcing and competitive pricing.",
    path: "/",
  });

  return (
    <div data-testid="home-page">
      <Hero />

      {/* Trust + brands strip */}
      <section className="border-y border-offwhite/10 bg-nearblack" data-testid="trust-strip">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
          {TRUST.map((t, i) => (
            <Reveal key={t.label} delay={i * 0.08}>
              <div className={`py-10 lg:py-12 lg:px-8 ${i !== 0 ? "lg:border-l border-offwhite/10" : ""} border-t md:border-t-0 border-offwhite/10`}>
                <p className="tech-label text-strike">{t.label}</p>
                <p className="text-offwhite/70 mt-3 text-sm">{t.sub}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* What we do */}
      <section id="what-we-do" className="py-24 lg:py-36 relative scroll-mt-24" data-testid="what-we-do">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-10">
          <div className="max-w-4xl mb-20">
            <TechLabel>What We Do</TechLabel>
            <h2 className="font-display font-bold tracking-tight text-offwhite text-2xl md:text-4xl leading-[1.15] mt-6">
              <Reveal>One supplier for drilling tools, components, spare and wear parts, consumables that keep mining equipment running , with reliable suppliers and competitive pricing.</Reveal>
            </h2>
          </div>

          <div className="space-y-16 lg:space-y-24">
            {WHAT_WE_DO.map((c, i) => (
              <div key={c.tag} className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center" data-testid={`wwd-row-${i}`}>
                <Reveal delay={0.05} className={i % 2 === 1 ? "lg:order-2" : ""}>
                  <div className="relative overflow-hidden border border-offwhite/10">
                    <img src={c.img} alt={c.title} loading="lazy" decoding="async" className="w-full aspect-[3/2] object-cover" data-testid={`wwd-img-${i}`} />
                  </div>
                </Reveal>
                <Reveal delay={0.15} className={i % 2 === 1 ? "lg:order-1" : ""}>
                  <div>
                    <div className="flex items-center gap-4">
                      <span className="font-mono-tech text-sm text-strike">0{i + 1}</span>
                      <span className="h-px flex-1 bg-offwhite/15" />
                    </div>
                    <p className="tech-label text-mutedgray mt-6">{c.tag}</p>
                    <h3 className="font-display text-3xl md:text-4xl font-bold text-offwhite mt-4 leading-tight">{c.title}</h3>
                    <p className="text-mutedgray mt-6 text-lg leading-relaxed">{c.copy}</p>
                  </div>
                </Reveal>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Brand logo roller */}
      <div className="py-8 lg:py-10 border-b border-offwhite/10 overflow-hidden bg-nearblack" data-testid="brands-marquee">
        <div className="marquee-wrap overflow-hidden">
          <div className="marquee-track items-center">
            {[...BRANDS, ...BRANDS].map((b, i) => (
              <div
                key={b.name + i}
                className="mx-4 shrink-0 flex items-center justify-center bg-white border border-offwhite/10 h-24 w-56 px-8"
                data-testid={`brand-logo-${b.name.toLowerCase()}`}
              >
                <img src={b.logo} alt={b.name} loading="lazy" decoding="async" className="max-h-12 w-auto object-contain" />
              </div>
            ))}
          </div>
        </div>
      </div>

      <CTASection />
    </div>
  );
}
